import { Form, Row, Col, Accordion } from "react-bootstrap";
import { useForm, FormProvider } from "react-hook-form";
import { FileUpload, Input, Select, PageWrapper } from "../../components";
import { useTranslation } from "react-i18next";
import { Pages, DriverApplicationStatus, DriverType } from '../../shared/enums'
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { getDriverApplicationDetail, updateDriverApplication } from "../../api/driver-application";
import { getVehicleTypeList } from "../../api/vehicle-type";
import useNavigationPrompt from "../../hooks/useNavigationPrompt";
import _ from "lodash";
import dayjs from "dayjs";
import { RiEdit2Fill } from "react-icons/ri";
import Button from '../../components/common/Button';

const DriverApplicationDetail = ({ }) => {
    const { t } = useTranslation()
    const form = useForm()
    const navigate = useNavigate()
    const { id } = useParams()
    const [vehicleTypes, setVehicleTypes] = useState([])
    const [btnLoading, setBtnLoading] = useState(false)
    const [isDirty, setIsDirty] = useState(false)
    const driverType = form.watch("driverType");
    const showVehicle = driverType === DriverType.E_HAILING;

    useNavigationPrompt(isDirty);

    // Watch for form changes
    useEffect(() => {
        const subscription = form.watch((value, { name, type }) => {
            if (type === 'change') {
                setIsDirty(true);
            }
        });
        return () => subscription.unsubscribe();
    }, [form.watch]);

    useEffect(() => {
        getData()
        form.reset({ countryCode: "+60" })
        getVehicleTypes()
    }, [])

    useEffect(() => {
        if (!showVehicle) {
            form.unregister("vehicleRegNo");
            form.unregister("vehicleBrand");
            form.unregister("vehicleModel");
            form.unregister("vehicleColor");
            form.unregister("registrationDate");
            form.unregister("grant");
            form.unregister("roadTax");
            form.unregister("roadTaxExpiryDate");
            form.unregister("evp");
            form.unregister("vehicleFront");
            form.unregister("vehicleBack");
            form.unregister("evpExpiryDate");
            form.unregister("insuranceExpiryDate");
            form.unregister("seatCapacity");
            form.unregister("vehicleType");
            form.unregister("psvExpiryDate");
            form.unregister("psvNumber");
            form.unregister("psvLicenseFront");
        }
    }, [showVehicle]);

    const getVehicleTypes = async (vehicleType = null) => {
        const response = await getVehicleTypeList({ skip: 0, limit: 1000 })
        const options = response.items.filter(vehicleType => vehicleType.id != id).map(vehicleType => ({ label: vehicleType.name, value: vehicleType.id }))
        setVehicleTypes(options)
        if (vehicleType) {
            form.reset({ ...vehicleType, crossVehicles: options.filter(e => vehicleType.crossVehicles.includes(e.value)) })
        }
    }

    const getData = async () => {
        const response = await getDriverApplicationDetail(id)
        form.reset({
            ...response,
            ..._.omit(response?.vehicle, ['status', 'createdAt', 'updatedAt', 'createdBy', 'updatedBy']),
            drivingLicenseExpiryDate: dayjs(response?.drivingLicenseExpiryDate).format("YYYY-MM-DD"),
            psvExpiryDate: dayjs(response?.psvExpiryDate).format("YYYY-MM-DD"),
            registrationDate: dayjs(response?.vehicle?.registrationDate).format("YYYY-MM-DD"),
            roadTaxExpiryDate: dayjs(response?.vehicle?.roadTaxExpiryDate).format("YYYY-MM-DD"),
            evpExpiryDate: dayjs(response?.vehicle?.evpExpiryDate).format("YYYY-MM-DD"),
            insuranceExpiryDate: dayjs(response?.vehicle?.insuranceExpiryDate).format("YYYY-MM-DD"),
            vehicleFront: response?.vehicle?.attachments[0],
            vehicleBack: response?.vehicle?.attachments[1]
        })
    }

    const update = async (data) => {
        setBtnLoading(true)
        const response = await updateDriverApplication(_.omit(data, ['id', 'createdAt', 'updatedAt', 'createdBy', 'updatedBy', 'vehicle', 'success', 'user', 'images', 'attachments']))
        if (response.success) {
            setIsDirty(false);
            navigate(Pages.DriverApplication)
        }
        setBtnLoading(false)
    }

    return <PageWrapper
        headerProps={{
            title: "EDIT_DRIVER_APPLICATION",
            rightElement: <Button
                type="submit"
                variant="primary"
                icon={<RiEdit2Fill />}
                loading={btnLoading}
                onClick={() => form.handleSubmit(update)()}
            >
                {t("UPDATE")}
            </Button>
        }}
    >
        <FormProvider {...form}>
            <Form>
                <Accordion defaultActiveKey={'0'} className="mb-4">
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>{t("DRIVER_INFORMATION")}</Accordion.Header>
                        <Accordion.Body>
                            <Select
                                label={t("DRIVER_TYPE")}
                                name={"driverType"}
                                rules={{ required: t("DRIVER_TYPE_IS_REQUIRED") }}
                                options={Object.keys(DriverType).map(key => ({ label: t(key), value: DriverType[key] }))}
                                containerProps={{ style: { width: "120px" } }}
                            />
                            <Input label={t("FULL_NAME")} name={"fullName"} rules={{ required: t("FULL_NAME_IS_REQUIRED") }} />
                            <Input label={t("IC_NUMBER")} name={"icNumber"} rules={{ required: t("IC_NUMBER_IS_REQUIRED") }} />
                            <Input
                                label={t("EMAIL")}
                                name={"email"}
                                type="email"
                                placeholder={"e.g. email@example.com"}
                                rules={{
                                    required: t("EMAIL_IS_REQUIRED"), pattern: {
                                        value: /^[\w.-]+@([\w-]+\.)+[\w-]{2,4}$/,
                                        message: t("INVALID_EMAIL")
                                    }
                                }} />
                            <Row>
                                <Col style={{ flex: 0 }}>
                                    <Select
                                        label={t("COUNTRY_CODE")}
                                        name={"countryCode"}
                                        rules={{ required: t("COUNTRY_CODE_IS_REQUIRED") }}
                                        options={[
                                            { label: "+60", value: "+60" },
                                        ]}
                                        containerProps={{ style: { width: "120px" } }}
                                    />
                                </Col>
                                <Col style={{ flex: 1 }}>
                                    <Input
                                        label={t("PHONE_NUMBER")}
                                        name={"phoneNumber"}
                                        placeholder={"e.g. 123456789"}
                                        rules={{
                                            required: t("PHONE_NUMBER_IS_REQUIRED"),
                                            pattern: {
                                                value: /^[0-9]{8,12}$/, // A basic 10-digit number pattern (adjust as needed)
                                                message: t("INVALID_PHONE_NUMBER")
                                            }
                                        }}
                                    />
                                </Col>
                            </Row>

                            <Row>
                                <Col>
                                    <Input label={t("AGE")} name={"age"} type="number" rules={{ required: t("AGE_IS_REQUIRED") }} />
                                </Col>
                                <Col>
                                    <Select
                                        label={t("GENDER")}
                                        name={"gender"}
                                        rules={{ required: t("GENDER_IS_REQUIRED") }}
                                        options={[
                                            { label: t("MALE"), value: "male" },
                                            { label: t("FEMALE"), value: "female" },
                                        ]}
                                    />
                                </Col>
                            </Row>

                            <Row>
                                <Col>
                                    <Input label={t("COUNTRY")} name={"country"} rules={{ required: t("COUNTRY_IS_REQUIRED") }} />
                                </Col>
                                <Col>
                                    <Input label={t("STATE")} name={"state"} rules={{ required: t("STATE_IS_REQUIRED") }} />
                                </Col>
                            </Row>

                            <FileUpload
                                label={t("SELFIE")}
                                name="selfie"
                                rules={{ required: t("SELFIE_IS_REQUIRED") }}
                            />
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>

                <Accordion defaultActiveKey={'0'} className="mb-4">
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>{t("IC")}</Accordion.Header>
                        <Accordion.Body>
                            <FileUpload
                                label={t("IC_FRONT")}
                                name="icFront"
                                rules={{ required: t("IC_FRONT_IS_REQUIRED") }}
                            />
                            <FileUpload
                                label={t("IC_BACK")}
                                name="icBack"
                                rules={{ required: t("IC_BACK_IS_REQUIRED") }}
                            />
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>

                <Accordion defaultActiveKey={'0'} className="mb-4">
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>{t("BANK")}</Accordion.Header>
                        <Accordion.Body>
                            <Input label={t("BANK_ACCOUNT_NUMBER")} name={"bankAccountNumber"} rules={{ required: t("BANK_ACCOUNT_NUMBER_IS_REQUIRED") }} />
                            <Input label={t("BANK_NAME")} name={"bankName"} rules={{ required: t("BANK_NAME_IS_REQUIRED") }} />
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>

                <Accordion defaultActiveKey={'0'} className="mb-4">
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>{t("DRIVING_LICENSE")}</Accordion.Header>
                        <Accordion.Body>
                            <FileUpload
                                label={t("DRIVING_LICENSE_FRONT")}
                                name="drivingLicenseFront"
                                rules={{ required: t("DRIVING_LICENSE_FRONT_IS_REQUIRED") }}
                            />
                            <FileUpload
                                label={t("DRIVING_LICENSE_BACK")}
                                name="drivingLicenseBack"
                                rules={{ required: t("DRIVING_LICENSE_BACK_IS_REQUIRED") }}
                            />
                            <Input label={t("DRIVING_LICENSE_EXPIRY_DATE")} name={"drivingLicenseExpiryDate"} type="date" rules={{ required: t("DRIVING_LICENSE_EXPIRY_DATE_IS_REQUIRED") }} />
                            <Select
                                label={t("DRIVING_LICENSE_CLASS_TYPE")}
                                name={"drivingLicenseClassType"}
                                rules={{ required: t("DRIVING_LICENSE_CLASS_TYPE_IS_REQUIRED") }}
                                options={[
                                    { label: t("D"), value: "D" },
                                    { label: t("DA"), value: "DA" },
                                    { label: t("B1"), value: "B1" },
                                    { label: t("B-Full"), value: "B-Full" },
                                ]}
                            />
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
                {showVehicle &&
                    <>
                        <Accordion defaultActiveKey={'0'} className="mb-4">
                            <Accordion.Item eventKey="0">
                                <Accordion.Header>{t("PSV")}</Accordion.Header>
                                <Accordion.Body>
                                    <FileUpload
                                        label={t("PSV_LICENSE_FRONT")}
                                        name="psvLicenseFront"
                                        rules={{ required: t("PSV_LICENSE_FRONT_IS_REQUIRED") }}
                                    />

                                    <Input label={t("PSV_EXPIRY_DATE")} name={"psvExpiryDate"} type="date" rules={{ required: t("PSV_EXPIRY_DATE_IS_REQUIRED") }} />
                                    <Input label={t("PSV_NUMBER")} name={"psvNumber"} rules={{ required: t("PSV_NUMBER_IS_REQUIRED") }} />
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>

                        <Accordion defaultActiveKey={'0'} className="mb-4">
                            <Accordion.Item eventKey="0">
                                <Accordion.Header>{t("VEHICLE")}</Accordion.Header>
                                <Accordion.Body>
                                    <Input label={t("REGISTRATION_NO")} name={"vehicleRegNo"} rules={{ required: t("REGISTRATION_NO_IS_REQUIRED") }} />
                                    <Input label={t("BRAND")} name={"vehicleBrand"} rules={{ required: t("BRAND_IS_REQUIRED") }} />
                                    <Input label={t("MODEL")} name={"vehicleModel"} rules={{ required: t("MODEL_IS_REQUIRED") }} />
                                    <Input label={t("COLOR")} name={"vehicleColor"} rules={{ required: t("COLOR_IS_REQUIRED") }} />
                                    <Input label={t("REGISTRATION_DATE")} name={"registrationDate"} type="date" rules={{ required: t("REGISTRATION_DATE_IS_REQUIRED") }} />

                                    <FileUpload
                                        label={t("GRANT")}
                                        name="grant"
                                        rules={{ required: t("GRANT_IS_REQUIRED") }}
                                    />

                                    <FileUpload
                                        label={t("ROAD_TAX")}
                                        name="roadTax"
                                        rules={{ required: t("ROAD_TAX_IS_REQUIRED") }}
                                    />

                                    <Input label={t("ROAD_TAX_EXPIRY_DATE")} name={"roadTaxExpiryDate"} type="date" rules={{ required: t("ROAD_TAX_EXPIRY_DATE_IS_REQUIRED") }} />

                                    <FileUpload
                                        label={t("EVP")}
                                        name="evp"
                                        rules={{ required: t("EVP_IS_REQUIRED") }}
                                    />
                                    <Input label={t("EVP_EXPIRY_DATE")} name={"evpExpiryDate"} type="date" rules={{ required: t("EVP_EXPIRY_DATE_IS_REQUIRED") }} />

                                    <FileUpload
                                        name="vehicleFront"
                                        label={t("VEHICLE_FRONT")}
                                        rules={{ required: t("VEHICLE_FRONT_IS_REQUIRED") }}
                                    />

                                    <FileUpload
                                        name="vehicleBack"
                                        label={t("VEHICLE_BACK")}
                                        rules={{ required: t("VEHICLE_BACK_IS_REQUIRED") }}
                                    />

                                    <Input label={t("INSURANCE_EXPIRY_DATE")} name={"insuranceExpiryDate"} type="date" rules={{ required: t("INSURANCE_EXPIRY_DATE_IS_REQUIRED") }} />

                                    <Input label={t("SEAT_CAPACITY")} name={"seatCapacity"} rules={{ required: t("SEAT_CAPACITY_IS_REQUIRED") }} />
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>
                    </>
                }
                <Select
                    label={t("STATUS")}
                    name={"status"}
                    rules={{ required: t("STATUS_IS_REQUIRED") }}
                    options={Object.keys(DriverApplicationStatus).map(key => ({ label: t(key), value: DriverApplicationStatus[key] }))}
                    containerProps={{ style: { width: "120px" } }}
                />

                {showVehicle &&
                    <Select
                        label={t("VEHICLE_TYPE")}
                        name={"vehicleType"}
                        rules={{ required: t("VEHICLE_TYPE_IS_REQUIRED") }}
                        options={vehicleTypes}
                    />
                }
            </Form>
        </FormProvider>
    </PageWrapper>
}

export default DriverApplicationDetail;